import React from "react";
import { motion } from "framer-motion";
import { Navigate, useLocation } from "react-router-dom";
import { MascotContainer } from "@/components/site/primitives";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import Logo from "@/components/site/Logo";
import { useTheme } from "@/hooks/use-theme";
import { useAuth } from "@/lib/useAuth";
import { getPostAuthDestination } from "@/lib/authRedirect";
import { AuthDecor, AuthBackdrop } from "@/components/auth/AuthDecor";
import SignInForm from "@/components/auth/SignInForm";
import beeRead from "@/assets/bee-read.png";

const EASE = [0.22, 1, 0.36, 1];
const line = { hidden: { y: "110%" }, show: (i) => ({ y: "0%", transition: { duration: 0.8, ease: EASE, delay: 0.1 + i * 0.1 } }) };
const soft = { hidden: { opacity: 0, y: 16 }, show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE, delay: 0.35 + i * 0.08 } }) };

export default function SignIn() {
  const { user, authStatus } = useAuth();
  const location = useLocation();
  const { dark, toggle } = useTheme();

  if (user && authStatus === "authenticated") return <Navigate to={getPostAuthDestination({ from: location.state?.from })} replace />;

  const headline = ["Read it.", "Scribble it.", "Share it."];
  const tones = ["text-ink", "text-coral", "text-ink"];

  return (
    <div className="relative min-h-screen overflow-hidden bg-cream" data-testid="signin-page">
      <AuthBackdrop className="pointer-events-none absolute inset-0 -z-10" />

      <div className="relative z-20 flex items-center justify-between px-5 pt-5 md:px-10 md:pt-8">
        <Logo to="/" size="md" testId="signin-logo" />
        <ThemeToggle dark={dark} toggle={toggle} />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-[1240px] items-center gap-10 px-5 py-8 md:grid-cols-2 md:gap-6 md:px-10 md:py-10 lg:gap-16 lg:py-14">
        {/* Left: brand / mascot — compact on mobile, full stage on desktop */}
        <div className="relative order-1 flex flex-col items-center text-center md:items-start md:text-left">
          <motion.span variants={soft} custom={0} initial="hidden" animate="show" className="u-eyebrow mb-3 hidden md:mb-4 md:inline-flex">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> Welcome back, reader
          </motion.span>

          <h1 className="font-display text-[clamp(1.9rem,6vw,3.5rem)] font-semibold leading-[1.04] tracking-tight">
            {headline.map((t, i) => (
              <span key={i} className="line-mask">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className={`inline-block ${tones[i]}`}>
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p variants={soft} custom={1} initial="hidden" animate="show" className="mt-3 max-w-sm text-[14px] leading-relaxed text-subtle md:mt-5 md:text-base">
            Sign in to pick up where you left off — discover stories, share what you're reading, and swap notes with fellow readers &amp; writers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
            className="relative mt-5 w-[46%] max-w-[180px] md:mt-10 md:w-[80%] md:max-w-[380px]"
          >
            <MascotContainer src={beeRead} alt="ScribbleReads bee mascot reading a book" className="relative z-10 mx-auto w-full" />
            <AuthDecor className="pointer-events-none absolute inset-0 hidden md:block" />
          </motion.div>
        </div>

        {/* Right: sign-in card */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          className="order-2 mx-auto w-full max-w-[420px] rounded-2xl border border-line bg-card/85 p-6 shadow-card backdrop-blur-xl sm:p-8"
        >
          <div className="mb-6 text-center md:text-left">
            <h2 className="font-display text-2xl font-semibold text-ink">Sign in</h2>
            <p className="mt-1.5 text-sm text-subtle">Good to see you again.</p>
          </div>
          <SignInForm />
        </motion.div>
      </div>
    </div>
  );
}
